/* eslint-disable max-len */
// YouTube video processing utilities

import type { UploadResult } from '../types';

/**
 * Extracts YouTube video ID from various URL formats
 */
export const extractYouTubeVideoId = (url: string): string | null =>
{
	// Single regex to handle all YouTube URL formats (standard, shorts, live, embed, etc.)
	const standardRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:shorts\/|live\/|watch\?v=|embed\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;

	const embedRegex = /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^"&?/\s]{11})/;

	const standardMatches = url.match(standardRegex);

	if (standardMatches && standardMatches[1])
	{
		return standardMatches[1];
	}

	const embedMatches = url.match(embedRegex);

	if (embedMatches && embedMatches[1])
	{
		return embedMatches[1];
	}

	return null;
};

/**
 * Generates a thumbnail URL from a YouTube video ID
 */
export const generateYouTubeThumbnail = (videoId: string): string =>
{
	return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
};

/**
 * Checks if a URL is a valid YouTube URL
 */
export const isYouTubeUrl = (url: string): boolean =>
{
	return extractYouTubeVideoId(url) !== null;
};

/**
 * Processes a YouTube URL to extract metadata
 */
export const processYouTubeUrl = (url: string): UploadResult | null =>
{
	const videoId = extractYouTubeVideoId(url);

	if (!videoId)
	{
		return null;
	}

	// Determine if it's an embedded URL
	const isEmbedded = url.includes('/embed/');

	// Create a filename-friendly version of the video ID
	const title = `video-${videoId}`;

	return {
		url,
		source         : 'embeddedLink',
		videoId,
		thumbnail      : generateYouTubeThumbnail(videoId),
		fileType       : 'youtube',
		title,
		isEmbedded,
		platform       : 'youtube',
		readyForUpload : true
	};
};

/**
 * Prepares YouTube video data for upload
 */
export const prepareVideoForUpload = (videoData: UploadResult, companyId?: string) =>
{
	if (!videoData.readyForUpload || !companyId)
	{
		return null;
	}

	// Prepare data structure expected by handleYouTubeVideoUpload
	return {
		videoUrl     : videoData.url,
		videoId      : videoData.videoId || '',
		thumbnailUrl : videoData.thumbnail || '',
		title        : videoData.title,
		source       : videoData.source,
		companyId
	};
};